import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { ChevronRight, Loader2, Mail, Plus, Trash2 } from "lucide-react";
import { Layout } from "../pages/Layout";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Checkbox } from "~/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "~/components/ui/card";
import {
  Field,
  FieldLabel,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldSet,
  FieldLegend,
} from "~/components/ui/field";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/components/ui/accordion";

export const Route = createFileRoute("/components")({
  head: () => ({
    meta: [
      { title: "Components — Lotin.uz" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ComponentsPage,
});

function ComponentsPage() {
  const [radioValue, setRadioValue] = useState("option-1");
  const [selectValue, setSelectValue] = useState("");
  const [checked1, setChecked1] = useState(false);
  const [checked2, setChecked2] = useState(true);

  return (
    <Layout title="Components">
      <div className="container mx-auto max-w-4xl px-4 py-8 space-y-8">
        <h1 className="text-4xl font-bold">Components</h1>

        {/* Buttons */}
        <Card>
          <CardHeader>
            <CardTitle>Button</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Variants */}
            <div>
              <p className="text-sm text-muted-foreground mb-3">Variants</p>
              <div className="flex flex-wrap gap-2">
                <Button>Default</Button>
                <Button variant="secondary">Secondary</Button>
                <Button variant="outline">Outline</Button>
                <Button variant="ghost">Ghost</Button>
                <Button variant="destructive">Destructive</Button>
                <Button variant="link">Link</Button>
              </div>
            </div>

            {/* Sizes */}
            <div>
              <p className="text-sm text-muted-foreground mb-3">Sizes</p>
              <div className="flex flex-wrap items-center gap-2">
                <Button size="xs">Extra Small</Button>
                <Button size="sm">Small</Button>
                <Button size="default">Default</Button>
                <Button size="lg">Large</Button>
                <Button size="icon"><Plus /></Button>
                <Button size="icon-xs"><Plus /></Button>
                <Button size="icon-sm"><Plus /></Button>
                <Button size="icon-lg"><Plus /></Button>
              </div>
            </div>

            {/* With icons */}
            <div>
              <p className="text-sm text-muted-foreground mb-3">With Icons</p>
              <div className="flex flex-wrap gap-2">
                <Button><Mail /> Login with Email</Button>
                <Button variant="outline"><ChevronRight /> Next</Button>
                <Button variant="destructive"><Trash2 /> Delete</Button>
                <Button disabled><Loader2 className="animate-spin" /> Loading</Button>
              </div>
            </div>

            {/* Disabled */}
            <div>
              <p className="text-sm text-muted-foreground mb-3">Disabled</p>
              <div className="flex flex-wrap gap-2">
                <Button disabled>Default</Button>
                <Button variant="secondary" disabled>Secondary</Button>
                <Button variant="outline" disabled>Outline</Button>
                <Button variant="ghost" disabled>Ghost</Button>
                <Button variant="destructive" disabled>Destructive</Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Form Elements */}
        <Card>
          <CardHeader>
            <CardTitle>Form Elements</CardTitle>
            <CardDescription>Input, Select, Checkbox, RadioGroup with Field components</CardDescription>
          </CardHeader>
          <CardContent>
            <FieldGroup>
              {/* Text Input */}
              <Field>
                <FieldLabel htmlFor="name">Name</FieldLabel>
                <Input id="name" placeholder="Enter your name" />
              </Field>

              {/* Email Input */}
              <Field>
                <FieldLabel htmlFor="email">Email</FieldLabel>
                <Input id="email" type="email" placeholder="email@example.com" />
                <FieldDescription>We'll never share your email.</FieldDescription>
              </Field>

              {/* Password Input */}
              <Field>
                <FieldLabel htmlFor="password">Password</FieldLabel>
                <Input id="password" type="password" placeholder="Enter password" />
              </Field>

              {/* Input with error */}
              <Field data-invalid="true">
                <FieldLabel htmlFor="username">Username</FieldLabel>
                <Input id="username" defaultValue="ab" aria-invalid />
                <FieldError>Username must be at least 3 characters.</FieldError>
              </Field>

              {/* Disabled Input */}
              <Field>
                <FieldLabel htmlFor="disabled">Disabled</FieldLabel>
                <Input id="disabled" disabled placeholder="Disabled input" />
              </Field>

              {/* Select */}
              <Field>
                <FieldLabel>Country</FieldLabel>
                <Select value={selectValue} onValueChange={setSelectValue}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a country" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="uz">Uzbekistan</SelectItem>
                      <SelectItem value="us">United States</SelectItem>
                      <SelectItem value="uk">United Kingdom</SelectItem>
                      <SelectItem value="de">Germany</SelectItem>
                      <SelectItem value="jp">Japan</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <FieldDescription>Select your country of residence.</FieldDescription>
              </Field>

              {/* Checkboxes */}
              <FieldSet>
                <FieldLegend variant="label">Notifications</FieldLegend>
                <Field orientation="horizontal">
                  <Checkbox
                    id="check-email"
                    checked={checked1}
                    onCheckedChange={(v) => setChecked1(v === true)}
                  />
                  <FieldLabel htmlFor="check-email">Email notifications</FieldLabel>
                </Field>
                <Field orientation="horizontal">
                  <Checkbox
                    id="check-sms"
                    checked={checked2}
                    onCheckedChange={(v) => setChecked2(v === true)}
                  />
                  <FieldLabel htmlFor="check-sms">SMS notifications</FieldLabel>
                </Field>
                <Field orientation="horizontal">
                  <Checkbox id="check-disabled" disabled />
                  <FieldLabel htmlFor="check-disabled">Disabled option</FieldLabel>
                </Field>
              </FieldSet>

              {/* Radio Group */}
              <FieldSet>
                <FieldLegend variant="label">Plan</FieldLegend>
                <RadioGroup value={radioValue} onValueChange={setRadioValue}>
                  <Field orientation="horizontal">
                    <RadioGroupItem value="option-1" id="plan-free" />
                    <FieldLabel htmlFor="plan-free">Free</FieldLabel>
                  </Field>
                  <Field orientation="horizontal">
                    <RadioGroupItem value="option-2" id="plan-pro" />
                    <FieldLabel htmlFor="plan-pro">Pro</FieldLabel>
                  </Field>
                  <Field orientation="horizontal">
                    <RadioGroupItem value="option-3" id="plan-enterprise" />
                    <FieldLabel htmlFor="plan-enterprise">Enterprise</FieldLabel>
                  </Field>
                </RadioGroup>
              </FieldSet>

              {/* Textarea */}
              <Field>
                <FieldLabel htmlFor="message">Message</FieldLabel>
                <textarea
                  id="message"
                  placeholder="Type your message here"
                  className="border border-input rounded-lg px-3 py-2 text-sm"
                  rows={4}
                />
                <FieldDescription>Max 500 characters.</FieldDescription>
              </Field>
            </FieldGroup>
          </CardContent>
          <CardFooter className="justify-end gap-2">
            <Button variant="outline">Cancel</Button>
            <Button>Submit</Button>
          </CardFooter>
        </Card>

        {/* Accordion */}
        <Card>
          <CardHeader>
            <CardTitle>Accordion</CardTitle>
          </CardHeader>
          <CardContent>
            <Accordion type="single" collapsible>
              <AccordionItem value="item-1">
                <AccordionTrigger>Is it accessible?</AccordionTrigger>
                <AccordionContent>
                  Yes. It adheres to the WAI-ARIA design pattern.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2">
                <AccordionTrigger>Is it styled?</AccordionTrigger>
                <AccordionContent>
                  Yes. It comes with default styles that matches the other components' aesthetic.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-3">
                <AccordionTrigger>Is it animated?</AccordionTrigger>
                <AccordionContent>
                  Yes. It's animated by default, but you can disable it if you prefer.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
